import React, { useState, useEffect, useRef } from 'react';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import { companyService } from '../services/companyService';
import './Companies.css';

const Companies = () => {
  const [companies, setCompanies] = useState([]);
  const [filteredCompanies, setFilteredCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [showApiKeyModal, setShowApiKeyModal] = useState(false);
  const [newApiKey, setNewApiKey] = useState('');

  const [showLogoModal, setShowLogoModal] = useState(false);
  const [selectedCompanyForLogo, setSelectedCompanyForLogo] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null); // État ajouté
  const [previewUrl, setPreviewUrl] = useState(''); // État pour l'aperçu
  const fileInputRef = useRef(null); // Ref pour l'input file

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedType, setSelectedType] = useState('');
  const [selectedSize, setSelectedSize] = useState('');

  // Dans l'initial state, changez 'sme' par 'micro'
  const [newCompany, setNewCompany] = useState({
        name: '',
        email: '',
        phone: '',
        address: '',
        size: 'micro', // Changé de 'sme' à 'micro'
        type: 'client',
        website: '',
        isActive: true
      });




  // Charger les entreprises
  const fetchCompanies = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await companyService.getCompanies();
      
      // Normaliser les données pour éviter les undefined
      const normalizedCompanies = (data.companies || []).map(company => ({
        ...company,
        type: company.type || 'client',
        size: company.size || 'micro',
        name: company.name || '',
        email: company.email || '',
        phone: company.phone || '',
        address: company.address || '',
        apiKey: company.apiKey || '',
        logoUrl: company.logoUrl || '', // Ajout du logoUrl
        isActive: company.isActive !== undefined ? company.isActive : true,
        employeeCount: company.employeeCount || 0
      }));
      
      setCompanies(normalizedCompanies);
      setFilteredCompanies(normalizedCompanies);
    } catch (err) {
      setError(err.message || 'Erreur lors du chargement des entreprises');
      console.error('Erreur fetch companies:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCompanies();
  }, []);

  // Filtrer les entreprises
  useEffect(() => {
    let filtered = companies;
    
    if (searchTerm.trim() !== '') {
      filtered = filtered.filter(company =>
        (company.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (company.email || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (company.phone || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (company.address || '').toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    if (selectedType) {
      filtered = filtered.filter(company => (company.type || '') === selectedType);
    }
    
    if (selectedSize) {
      filtered = filtered.filter(company => (company.size || '') === selectedSize);
    }
    
    setFilteredCompanies(filtered);
  }, [searchTerm, selectedType, selectedSize, companies]);

  // Fonction pour réinitialiser l'upload de logo
  const resetLogoUpload = () => {
    setSelectedFile(null);
    setPreviewUrl('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Ouvrir le modal d'upload de logo
  const handleUploadLogo = (companyId) => {
    const company = companies.find(c => c._id === companyId);
    if (company) {
      setSelectedCompanyForLogo(company);
      setShowLogoModal(true);
      resetLogoUpload(); // Réinitialiser l'upload précédent
    }
  };

  // Gérer la sélection de fichier
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Vérifier la taille (max 1MB)
    if (file.size > 1 * 1024 * 1024) {
      setError('Le fichier est trop volumineux (max 1MB)');
      resetLogoUpload();
      return;
    }

    // Vérifier le type de fichier
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'];
    if (!allowedTypes.includes(file.type)) {
      setError('Format non supporté. Formats acceptés: JPG, PNG, GIF, WebP, SVG');
      resetLogoUpload();
      return;
    }

    setSelectedFile(file);
    setError(''); // Effacer les erreurs précédentes

    // Créer un aperçu
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreviewUrl(reader.result);
    };
    reader.readAsDataURL(file);
  };

  // Gérer l'upload du logo
  const handleLogoUpload = async (e) => {
    e.preventDefault();
    
    if (!selectedFile || !selectedCompanyForLogo) {
      setError('Veuillez sélectionner un fichier');
      return;
    }

    try {
      setError('');
      const formData = new FormData();
      formData.append('logo', selectedFile);
      
      const response = await companyService.uploadLogo(selectedCompanyForLogo._id, formData);
      
      // Mettre à jour la liste des entreprises
      if (response.success) {
        setCompanies(prev => 
          prev.map(c => 
            c._id === selectedCompanyForLogo._id 
              ? { ...c, logoUrl: response.logoUrl }
              : c
          )
        );
        
        alert(`✅ Logo uploadé pour ${selectedCompanyForLogo.name}`);
        setShowLogoModal(false);
        setSelectedCompanyForLogo(null);
        resetLogoUpload();
      } else {
        setError(response.message || 'Erreur lors de l\'upload du logo');
      }
    } catch (err) {
      setError(err.message || 'Erreur lors de l\'upload du logo');
      console.error('Erreur upload logo:', err);
    }
  };



  // Gérer la suppression du logo - VERSION CORRIGÉE
const handleDeleteLogo = async (companyId, companyName) => {
  if (!window.confirm(`Êtes-vous sûr de vouloir supprimer le logo de "${companyName}" ?`)) {
    return;
  }

  try {
    setError('');
    
    // Utilisez fetch directement OU corrigez comme suit :
    const response = await companyService.deleteLogo(companyId);
    
    console.log('📥 Réponse deleteLogo:', response);
    
    // Votre apiService retourne directement les données, pas {data, success}
    // Donc response est déjà {success: true, message: '...', updated: true}
    
    if (response && response.success === true) {
      // 1. Rafraîchir les données
      await fetchCompanies();
      
      // 2. Fermer le modal
      setShowLogoModal(false);
      setSelectedCompanyForLogo(null);
      resetLogoUpload();
      
      alert('✅ Logo supprimé avec succès');
    } else {
      setError(response?.message || 'Erreur lors de la suppression du logo');
    }
  } catch (err) {
    console.error('❌ Erreur delete logo détaillée:', err);
    setError(err.message || 'Erreur lors de la suppression du logo');
  }
};




  // Ajouter une entreprise
// Ajouter une entreprise - CORRIGÉ
const handleAddCompany = async (e) => {
  e.preventDefault();
  try {
    setError('');
    const data = await companyService.createCompany(newCompany);
    
    if (data.success && data.data) {
      // Rafraîchir depuis l'API pour avoir les données à jour
      await fetchCompanies();
      
      // Réinitialiser le formulaire
      setNewCompany({
        name: '',
        email: '',
        phone: '',
        address: '',
        size: 'micro', // Changé à 'micro'
        type: 'client',
        website: '',
        isActive: true
      });
      setShowAddForm(false);
      
      alert('✅ Entreprise créée avec succès');
    }
  } catch (err) {
    setError(err.message || 'Erreur lors de la création');
    console.error('Erreur create company:', err);
  }
};

  // Régénérer la clé API
// Régénérer la clé API - CORRIGÉ
const handleRegenerateApiKey = async (companyId) => {
  const company = companies.find(c => c._id === companyId);
  
  if (!company) return;
  
  if (!window.confirm(`Êtes-vous sûr de vouloir régénérer la clé API de "${company.name}" ?\n\nL'ancienne clé ne fonctionnera plus.`)) {
    return;
  }

  try {
    setError('');
    const result = await companyService.regenerateApiKey(companyId);
    
    // Votre apiService retourne directement {success, apiKey, ...}
    // Pas besoin de result.data?.apiKey
    if (result && result.success === true) {
      setNewApiKey(result.apiKey || result.data?.apiKey);
      setShowApiKeyModal(true);
      
      // Rafraîchir depuis l'API
      await fetchCompanies();
    } else {
      setError(result?.message || 'Erreur lors de la régénération de la clé API');
    }
  } catch (err) {
    setError(err.message || 'Erreur lors de la régénération de la clé API');
    console.error('Erreur regenerate api key:', err);
  }
};

  // Copier dans le presse-papier
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      alert('Clé API copiée dans le presse-papier !');
    }).catch(err => {
      console.error('Erreur lors de la copie:', err);
    });
  };

  // Copier la clé API
  const handleCopyApiKey = (apiKey) => {
    copyToClipboard(apiKey);
  };

  // Ouvrir le formulaire de modification
  const handleEditCompany = (company) => {
    // Normaliser l'entreprise avant modification
    const normalizedCompany = {
      ...company,
      type: company.type || 'client',
      size: company.size || 'sme',
      name: company.name || '',
      email: company.email || '',
      phone: company.phone || '',
      address: company.address || ''
    };
    setEditingCompany(normalizedCompany);
    setShowEditForm(true);
  };

  // Modifier une entreprise
// Modifier une entreprise - CORRIGÉ
const handleUpdateCompany = async (e) => {
  e.preventDefault();
  try {
    setError('');
    
    // Normaliser les données avant envoi
    const companyToUpdate = {
      ...editingCompany,
      type: editingCompany.type || 'client',
      size: editingCompany.size || 'micro' // Changé à 'micro'
    };
    
    const response = await companyService.updateCompany(
      editingCompany._id, 
      companyToUpdate
    );
    
    if (response.success) {
      // Rafraîchir depuis l'API
      await fetchCompanies();
      
      setShowEditForm(false);
      setEditingCompany(null);
      
      alert('✅ Entreprise modifiée avec succès');
    } else {
      setError(response.message || 'Erreur lors de la modification');
    }
  } catch (err) {
    setError(err.message || 'Erreur lors de la modification');
    console.error('Erreur update company:', err);
  }
};
  // Supprimer une entreprise
// Supprimer une entreprise - CORRIGÉ
const handleDeleteCompany = async (id) => {
  const company = companies.find(c => c._id === id);
  
  if (!company) return;
  
  if (!window.confirm(`Êtes-vous sûr de vouloir supprimer l'entreprise "${company.name}" ?\n\nTous les employés et offres associés seront également désactivés.`)) {
    return;
  }

  try {
    setError('');
    await companyService.deleteCompany(id);
    
    // Rafraîchir depuis l'API
    await fetchCompanies();
    
    alert('✅ Entreprise supprimée avec succès');
  } catch (err) {
    setError(err.message || 'Erreur lors de la suppression');
    console.error('Erreur delete company:', err);
  }
};

  // AJOUTEZ CETTE FONCTION AU DÉBUT DES 3 FONCTIONS SUIVANTES
  const getTypeIcon = (type) => {
    const companyType = type || 'client'; // LIGNE AJOUTÉE
    switch(companyType) {
      case 'client': return '👥';
      case 'partner': return '🤝';
      case 'both': return '🔄';
      default: return '🏢';
    }
  };

  const getTypeLabel = (type) => {
    const companyType = type || 'client'; // LIGNE AJOUTÉE
    switch(companyType) {
      case 'client': return 'Client';
      case 'partner': return 'Partenaire';
      case 'both': return 'Client & Partenaire';
      default: return companyType;
    }
  };


const CompanyAvatar = ({ company }) => {
                          if (company.logoUrl) {
                            return (
                              <div className="company-avatar">
                                <img 
                                  src={company.logoUrl} 
                                  alt={`Logo ${company.name}`} 
                                  className="company-logo"
                                  onError={(e) => {
                                    // Si le logo ne charge pas, afficher l'icône par défaut
                                    e.target.style.display = 'none';
                                    e.target.parentElement.innerHTML = `<span class="avatar-icon">${getTypeIcon(company.type)}</span>`;
                                  }}
                                />
                              </div>
                            );
                          }
                          
                          return (
                            <div className="company-avatar">
                              <span className="avatar-icon">{getTypeIcon(company.type)}</span>
                            </div>
                          );};


  const getSizeLabel = (size) => {
  const companySize = size || 'micro';
  
  switch(companySize) {
    case 'self_employed': return '👤 Auto-entrepreneur';
    case 'micro': return '🚀 Micro-entreprise';
    case 'small': return '🏢 Petite entreprise';
    case 'medium': return '🏭 Moyenne entreprise';
    case 'large': return '🏛️ Grande entreprise';
    case 'corporate': return '🌍 Corporation'; // ICI: 'corporate' pas 'corporation'
    default: return companySize;
  }
};

  // Fonction pour réinitialiser les filtres
  const handleClearFilters = () => {
    setSearchTerm('');
    setSelectedType('');
    setSelectedSize('');
  };

  if (loading) {
    return (
      <div className="companies">
        <div className="page-header">
          <div className="page-title">
            <h1>Gestion des Entreprises</h1>
            <p>Chargement...</p>
          </div>
        </div>
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Chargement des entreprises...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="companies">


      <div className="page-header">
        <div className="page-title">
          <h1>Gestion des Entreprises</h1>
          <p>Gérez les entreprises partenaires et clients ({companies.length} entreprise{companies.length > 1 ? 's' : ''})</p>
        </div>
        <div className="header-actions">
          <Button 
            variant="secondary" 
            size="lg"
            onClick={fetchCompanies}
            disabled={loading}
          >
            <span className="button-icon">🔄</span>
            Rafraîchir
          </Button>
          <Button 
            variant="primary" 
            size="lg"
            onClick={() => setShowAddForm(true)}
          >
            <span className="button-icon">➕</span>
            Ajouter une entreprise
          </Button>
        </div>
      </div>
      

      <Card className="filters-card" padding="md">
        <div className="filters-container">
          <div className="search-container">
            <div className="search-icon">🔍</div>
            <input
              type="text"
              placeholder="Rechercher une entreprise par nom, email, téléphone ou adresse..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
            {searchTerm && (
              <button 
                className="clear-search"
                onClick={() => setSearchTerm('')}
              >
                ✕
              </button>
            )}
          </div>
          
          <div className="filter-group">
            <select
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value)}
              className="filter-select"
            >
              <option value="">Tous les types</option>
              <option value="client">👥 Client</option>
              <option value="partner">🤝 Partenaire</option>
              <option value="both">🔄 Les deux</option>
            </select>
          </div>
          
          <div className="filter-group">
            <select
              value={selectedSize}
              onChange={(e) => setSelectedSize(e.target.value)}
              className="filter-select"
            >
                <option value="self_employed">👤 Auto-entrepreneur (0-1)</option>
                <option value="micro">🚀 Micro-entreprise (2-9)</option>
                <option value="small">🏢 Petite entreprise (10-49)</option>
                <option value="medium">🏭 Moyenne entreprise (50-249)</option>
                <option value="large">🏛️ Grande entreprise (250-999)</option>
                <option value="corporate">🌍 Corporation (1000+)</option>
            </select>
          </div>
          
          {(searchTerm || selectedType || selectedSize) && (
            <Button 
              variant="secondary" 
              size="sm"
              onClick={handleClearFilters}
              className="clear-filters-btn"
            >
              Effacer les filtres
            </Button>
          )}
        </div>
        
        {(searchTerm || selectedType || selectedSize) && (
          <div className="filters-info">
            <span className="results-count">
              {filteredCompanies.length} entreprise{filteredCompanies.length > 1 ? 's' : ''} trouvée{filteredCompanies.length > 1 ? 's' : ''}
            </span>
          </div>
        )}
      </Card>

      {error && (
        <Card className="error-card" padding="md">
          <div className="error-message">
            <span>❌</span>
            {error}
            <button onClick={() => setError('')} className="error-close">✕</button>
          </div>
        </Card>
      )}

      {showApiKeyModal && (
        <div className="modal-overlay">
          <Card className="modal" padding="xl" borderRadius="xl">
            <div className="modal-header">
              <h2>Nouvelle Clé API Générée</h2>
              <button 
                className="close-button"
                onClick={() => setShowApiKeyModal(false)}
              >
                ✕
              </button>
            </div>
            
            <div className="apikey-modal-content">
              <div className="apikey-warning">
                <span>⚠️</span>
                <p>Cette clé API est confidentielle — gardez-la en sécurité et ne la partagez pas.</p>
                <p><strong>L'ancienne clé ne fonctionnera plus.</strong></p>
              </div>
              
              <div className="apikey-display">
                <label className="form-label">Clé API:</label>
                <div className="apikey-field">
                  <input
                    type="text"
                    value={newApiKey}
                    readOnly
                    className="form-input apikey-input"
                  />
                  <Button 
                    variant="secondary" 
                    size="sm"
                    onClick={() => copyToClipboard(newApiKey)}
                  >
                    📋 Copier
                  </Button>
                </div>
              </div>
              
              <div className="form-actions">
                <Button 
                  variant="primary"
                  onClick={() => setShowApiKeyModal(false)}
                >
                  J'ai noté la clé API
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}



      {/* Modal d'upload de logo */}
        {showLogoModal && selectedCompanyForLogo && (
              <div className="modal-overlay">
                <Card className="modal" padding="xl" borderRadius="xl">
                  <div className="modal-header">
                    <h2>
                      {selectedCompanyForLogo.logoUrl ? 'Changer le logo' : 'Ajouter un logo'}
                    </h2>
                    <button 
                      className="close-button"
                      onClick={() => {
                        setShowLogoModal(false);
                        setSelectedCompanyForLogo(null);
                        resetLogoUpload();
                      }}
                    >
                      ✕
                    </button>
                  </div>
                  
                  <div className="logo-modal-content">
                    <div className="logo-info">
                      <p>Entreprise: <strong>{selectedCompanyForLogo.name}</strong></p>
                      {selectedCompanyForLogo.logoUrl && (
                        <div className="current-logo-section">
                          <p className="current-logo-label">Logo actuel:</p>
                          <div className="current-logo-preview">
                            <img 
                              src={selectedCompanyForLogo.logoUrl} 
                              alt="Logo actuel" 
                              className="current-logo"
                            />
                            <Button 
                              variant="danger" 
                              size="xs"
                              onClick={() => handleDeleteLogo(selectedCompanyForLogo._id, selectedCompanyForLogo.name)}
                            >
                              🗑️ Supprimer
                            </Button>
                          </div>
                        </div>
                      )}
                      <p className="logo-instructions">
                        Taille recommandée: 500x500px
                        <br />
                        Formats acceptés: JPG, PNG, GIF, WebP, SVG (max 1MB)
                      </p>
                    </div>
                    
                    <form onSubmit={handleLogoUpload} className="logo-form">
                      <div className="form-group">
                        <label className="form-label">Sélectionner une image</label>
                        <div className="file-upload-container">
                          <input
                            type="file"
                            id="logo-upload"
                            ref={fileInputRef}
                            accept=".jpg,.jpeg,.png,.gif,.webp,.svg"
                            className="file-input"
                            onChange={handleFileSelect}
                          />
                          <label htmlFor="logo-upload" className="file-upload-label">
                            <span className="upload-icon">📁</span>
                            <span className="upload-text">
                              {selectedFile ? selectedFile.name : 'Choisir un fichier'}
                            </span>
                          </label>
                        </div>
                        {selectedFile && (
                          <div className="file-info">
                            <span className="file-size">
                              {(selectedFile.size / 1024).toFixed(1)} KB
                            </span>
                            <button 
                              type="button" 
                              className="remove-file"
                              onClick={resetLogoUpload}
                            >
                              ✕
                            </button>
                          </div>
                        )}
                      </div>
                      
                      <div className="form-group">
                        <label className="form-label">Aperçu</label>
                        <div className="logo-preview">
                          {previewUrl ? (
                            <img 
                              src={previewUrl} 
                              alt="Aperçu du logo" 
                              className="preview-image"
                            />
                          ) : (
                            <div className="preview-placeholder">
                              <span className="preview-icon">🖼️</span>
                              <p>Aperçu du logo</p>
                            </div>
                          )}
                        </div>
                      </div>
                      
                      <div className="form-actions">
                        <Button 
                          type="button" 
                          variant="secondary"
                          onClick={() => {
                            setShowLogoModal(false);
                            setSelectedCompanyForLogo(null);
                            resetLogoUpload();
                          }}
                        >
                          Annuler
                        </Button>
                        <Button 
                          type="submit" 
                          variant="primary"
                          disabled={!selectedFile}
                        >
                          {selectedCompanyForLogo.logoUrl ? 'Changer le logo' : 'Uploader le logo'}
                        </Button>
                      </div>
                    </form>
                  </div>
                </Card>
              </div>
            )}

      {showAddForm && (
        <div className="modal-overlay">
          <Card className="modal" padding="xl" borderRadius="xl">
            <div className="modal-header">
              <h2>Nouvelle Entreprise</h2>
              <button 
                className="close-button"
                onClick={() => setShowAddForm(false)}
              >
                ✕
              </button>
            </div>
            
            <form onSubmit={handleAddCompany} className="company-form">
              <div className="form-group">
                <label className="form-label">Nom de l'entreprise *</label>
                <input
                  type="text"
                  value={newCompany.name}
                  onChange={(e) => setNewCompany({...newCompany, name: e.target.value})}
                  required
                  className="form-input"
                  placeholder="Ex: Sonatrach"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Email *</label>
                  <input
                    type="email"
                    value={newCompany.email}
                    onChange={(e) => setNewCompany({...newCompany, email: e.target.value})}
                    required
                    className="form-input"
                    placeholder="contact@entreprise.dz"
                  />
                </div>
                
                <div className="form-group">
                  <label className="form-label">Téléphone *</label>
                  <input
                    type="tel"
                    value={newCompany.phone}
                    onChange={(e) => setNewCompany({...newCompany, phone: e.target.value})}
                    required
                    className="form-input"
                    placeholder="+213 555 12 34 56"
                  />
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Adresse *</label>
                <input
                  type="text"
                  value={newCompany.address}
                  onChange={(e) => setNewCompany({...newCompany, address: e.target.value})}
                  required
                  className="form-input"
                  placeholder="Adresse complète"
                />
              </div>


              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Type *</label>
                  <select
                    value={newCompany.type}
                    onChange={(e) => setNewCompany({...newCompany, type: e.target.value})}
                    className="form-select"
                    required
                  >
                    <option value="client">👥 Client</option>
                    <option value="partner">🤝 Partenaire</option>
                    <option value="both">🔄 Client & Partenaire</option>
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">Taille *</label>
                  <select
                    value={newCompany.size}
                    onChange={(e) => setNewCompany({...newCompany, size: e.target.value})}
                    className="form-select"
                    required
                  >
                      <option value="self_employed">👤 Auto-entrepreneur (0-1)</option>
                      <option value="micro">🚀 Micro-entreprise (2-9)</option>
                      <option value="small">🏢 Petite entreprise (10-49)</option>
                      <option value="medium">🏭 Moyenne entreprise (50-249)</option>
                      <option value="large">🏛️ Grande entreprise (250-999)</option>
                      <option value="corporate">🌍 Corporation (1000+)</option>
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Site Web</label>
                <input
                  type="url"
                  value={newCompany.website}
                  onChange={(e) => setNewCompany({...newCompany, website: e.target.value})}
                  className="form-input"
                  placeholder="https://www.entreprise.dz"
                />
              </div>



              <div className="form-group">
                <label className="form-label checkbox-label">
                  <input
                    type="checkbox"
                    checked={newCompany.isActive}
                    onChange={(e) => setNewCompany({...newCompany, isActive: e.target.checked})}
                    className="form-checkbox"
                  />
                  <span>Entreprise active</span>
                </label>
              </div>

              <div className="form-actions">
                <Button 
                  type="button" 
                  variant="secondary"
                  onClick={() => setShowAddForm(false)}
                >
                  Annuler
                </Button>
                <Button type="submit" variant="primary">
                  Créer l'entreprise
                </Button>
              </div>
            </form>
          </Card>
        </div>
      )}

      {showEditForm && editingCompany && (
        <div className="modal-overlay">
          <Card className="modal" padding="xl" borderRadius="xl">
            <div className="modal-header">
              <h2>Modifier l'entreprise</h2>
              <button 
                className="close-button"
                onClick={() => {
                  setShowEditForm(false);
                  setEditingCompany(null);
                }}
              >
                ✕
              </button>
            </div>
            
            <form onSubmit={handleUpdateCompany} className="company-form">
              <div className="form-group">
                <label className="form-label">Nom de l'entreprise *</label>
                <input
                  type="text"
                  value={editingCompany.name || ''}
                  onChange={(e) => setEditingCompany({...editingCompany, name: e.target.value})}
                  required
                  className="form-input"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Email *</label>
                  <input
                    type="email"
                    value={editingCompany.email || ''}
                    onChange={(e) => setEditingCompany({...editingCompany, email: e.target.value})}
                    required
                    className="form-input"
                  />
                </div>
                
                <div className="form-group">
                  <label className="form-label">Téléphone *</label>
                  <input
                    type="tel"
                    value={editingCompany.phone || ''}
                    onChange={(e) => setEditingCompany({...editingCompany, phone: e.target.value})}
                    required
                    className="form-input"
                  />
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Adresse *</label>
                <input
                  type="text"
                  value={editingCompany.address || ''}
                  onChange={(e) => setEditingCompany({...editingCompany, address: e.target.value})}
                  required
                  className="form-input"
                />
              </div>



              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Type *</label>
                  <select
                    value={editingCompany.type || 'client'}
                    onChange={(e) => setEditingCompany({...editingCompany, type: e.target.value})}
                    className="form-select"
                    required
                  >
                    <option value="client">👥 Client</option>
                    <option value="partner">🤝 Partenaire</option>
                    <option value="both">🔄 Client & Partenaire</option>
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">Taille *</label>
                  <select
                    value={editingCompany.size || 'sme'}
                    onChange={(e) => setEditingCompany({...editingCompany, size: e.target.value})}
                    className="form-select"
                    required
                  >
                  <option value="self_employed">👤 Auto-entrepreneur (0-1)</option>
                  <option value="micro">🚀 Micro-entreprise (2-9)</option>
                  <option value="small">🏢 Petite entreprise (10-49)</option>
                  <option value="medium">🏭 Moyenne entreprise (50-249)</option>
                  <option value="large">🏛️ Grande entreprise (250-999)</option>
                  <option value="corporate">🌍 Corporation (1000+)</option>
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Site Web</label>
                <input
                  type="url"
                  value={editingCompany.website || ''}
                  onChange={(e) => setEditingCompany({...editingCompany, website: e.target.value})}
                  className="form-input"
                />
              </div>


              <div className="form-group">
                <label className="form-label checkbox-label">
                  <input
                    type="checkbox"
                    checked={editingCompany.isActive}
                    onChange={(e) => setEditingCompany({...editingCompany, isActive: e.target.checked})}
                    className="form-checkbox"
                  />
                  <span>Entreprise active</span>
                </label>
              </div>

              <div className="form-actions">
                <Button 
                  type="button" 
                  variant="secondary"
                  onClick={() => {
                    setShowEditForm(false);
                    setEditingCompany(null);
                  }}
                >
                  Annuler
                </Button>
                <Button type="submit" variant="primary">
                  Modifier l'entreprise
                </Button>
              </div>
            </form>
          </Card>
        </div>
      )}

      {/* CHANGEMENT CRITIQUE ICI - Ajouter des valeurs par défaut */}
      <div className="companies-grid">
        {filteredCompanies.map(company => {
          // Utiliser des valeurs par défaut pour chaque entreprise
          const companyType = company?.type || 'client';
          const companySize = company?.size || 'sme';
          const companyName = company?.name || 'Entreprise sans nom';

          return (
            
            <Card key={company._id} className="company-card" padding="lg">
              <div className="company-header">
                 <CompanyAvatar company={company} />
                <div className="company-info">
                  <h3 className="company-name">{companyName}</h3>
                  <span className={`type-badge ${companyType}`}>
                    {getTypeIcon(companyType)} {getTypeLabel(companyType)}
                  </span>
                </div>
              </div>
              
<div className="company-details">
  {/* Tous les spans directement dans .company-details */}

  <span className="detail-label">id :</span>

  <div className="api-key-container">
    <span className="api-key-value">
      {company._id  ? company._id  : 'Non existante'}
    </span>
    <Button 
      variant="secondary" 
      size="xs"
      onClick={() => handleCopyApiKey(company._id)}
      disabled={!company._id}
    >
      📋
    </Button>
  </div>


  <span className="detail-label">Email:</span>
  <span className="detail-value">{company.email || ''}</span>
  
  <span className="detail-label">Téléphone:</span>
  <span className="detail-value">{company.phone || ''}</span>
  
  <span className="detail-label">Adresse:</span>
  <span className="detail-value address">{company.address || ''}</span>
  
  <span className="detail-label">Taille:</span>
  <span className={`size-badge ${companySize}`}>
    {getSizeLabel(companySize)}
  </span>
  
  <span className="detail-label">Créée le:</span>
  <span className="detail-value">
    {company.createdAt ? new Date(company.createdAt).toLocaleDateString('fr-FR') : 'Date inconnue'}
  </span>
  
  <span className="detail-label">Employés:</span>
  <span className="detail-value employees-count">
    {(company.employeeCount || 0)} employé{(company.employeeCount || 0) > 1 ? 's' : ''}
  </span>
  
  <span className="detail-label">Clé API:</span>
  <div className="api-key-container">
    <span className="api-key-value">
      {company.apiKey ? company.apiKey.substring(0, 20) + '...' : 'Non générée'}
    </span>
    <Button 
      variant="secondary" 
      size="xs"
      onClick={() => handleCopyApiKey(company.apiKey)}
      disabled={!company.apiKey}
    >
      📋
    </Button>
  </div>
  
  <span className="detail-label">Statut:</span>
  <span className={`status-badge ${company.isActive ? 'active' : 'inactive'}`}>
    {company.isActive ? '🟢 Actif' : '🔴 Inactif'}
  </span>
</div>
              
              <div className="company-actions">
                <Button 
                  variant="secondary" 
                  size="sm"
                  onClick={() => handleEditCompany(company)}
                >
                  ✏️ Modifier
                </Button>
                
                <Button 
                  variant="secondary" 
                  size="sm"
                  onClick={() => handleRegenerateApiKey(company._id)}
                  disabled={!company.isActive}
                >
                  🔑 Régénérer API
                </Button>

              <Button 
              variant="secondary" 
              size="sm"
              onClick={() => handleUploadLogo(company._id)}
            >
              {company.logoUrl ? '🖼️ Changer logo' : '🖼️ Ajouter logo'}
                </Button>
                
                <Button 
                  variant="danger" 
                  size="sm"
                  onClick={() => handleDeleteCompany(company._id)}
                  disabled={company.isActive && ((company.employeeCount || 0) > 0)}
                >
                  🗑️ Supprimer
                </Button>
              </div>
            </Card>
          );
        })}
      </div>

      {filteredCompanies.length === 0 && !loading && (
        <Card className="empty-state">
          <div className="empty-icon">🏢</div>
          <h3 className="empty-title">
            {searchTerm || selectedType || selectedSize ? 'Aucune entreprise trouvée' : 'Aucune entreprise'}
          </h3>
          <p className="empty-text">
            {searchTerm 
              ? `Aucun résultat pour "${searchTerm}"`
              : selectedType || selectedSize
              ? `Aucune entreprise trouvée avec les filtres sélectionnés`
              : 'Commencez par ajouter votre première entreprise'
            }
          </p>
          <Button 
            variant="primary"
            onClick={() => {
              handleClearFilters();
              setShowAddForm(true);
            }}
          >
            ➕ Ajouter une entreprise
          </Button>
        </Card>
      )}
    </div>
  );
};

export default Companies;